import type { $Fetch } from 'ofetch';
import type {
  ContratoTipoGateway, ContratoTemplateGateway, CertificadoTemplateGateway,
  ListContratoTiposParams, ListContratoTemplatesParams, ListCertificadoTemplatesParams,
} from '@/application/port/out/TemplateGateways';
import type {
  ContratoTipo, ContratoTipoCommand, ContratoTemplate, ContratoTemplateCommand,
  CertificadoTemplate, CertificadoTemplateCommand,
} from '@/domain/model/Template';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateways dos templates (acadadmin slice). DELETE = 204 (sem assertApiSuccess).
 *
 * @since G.5.18
 */
export class HttpContratoTipoGateway implements ContratoTipoGateway {
  private readonly base = '/api/v1/admin/contrato-tipos';
  constructor(private readonly client: $Fetch) {}

  async list(params: ListContratoTiposParams): Promise<Page<ContratoTipo>> {
    const query: Record<string, string | number | boolean> = {
      onlyActive: params.onlyActive ?? false, page: params.page ?? 0, size: params.size ?? 50,
    };
    if (params.categoria) query.categoria = params.categoria;
    const res = await this.client<ApiResponse<Page<ContratoTipo>>>(this.base, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: ContratoTipoCommand): Promise<ContratoTipo> {
    const res = await this.client<ApiResponse<ContratoTipo>>(this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: ContratoTipoCommand): Promise<ContratoTipo> {
    const res = await this.client<ApiResponse<ContratoTipo>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async deactivate(id: string): Promise<void> {
    await this.client(`${this.base}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

export class HttpContratoTemplateGateway implements ContratoTemplateGateway {
  private readonly base = '/api/v1/admin/contrato-templates';
  constructor(private readonly client: $Fetch) {}

  async list(params: ListContratoTemplatesParams): Promise<Page<ContratoTemplate>> {
    const query: Record<string, string | number> = { page: params.page ?? 0, size: params.size ?? 50 };
    if (params.codigo) query.codigo = params.codigo;
    const res = await this.client<ApiResponse<Page<ContratoTemplate>>>(this.base, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }
  async get(id: string): Promise<ContratoTemplate> {
    const res = await this.client<ApiResponse<ContratoTemplate>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: ContratoTemplateCommand): Promise<ContratoTemplate> {
    const res = await this.client<ApiResponse<ContratoTemplate>>(this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: ContratoTemplateCommand): Promise<ContratoTemplate> {
    const res = await this.client<ApiResponse<ContratoTemplate>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async deactivate(id: string): Promise<void> {
    await this.client(`${this.base}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

export class HttpCertificadoTemplateGateway implements CertificadoTemplateGateway {
  private readonly base = '/api/v1/admin/certificado-templates';
  constructor(private readonly client: $Fetch) {}

  async list(params: ListCertificadoTemplatesParams): Promise<Page<CertificadoTemplate>> {
    const query: Record<string, string | number> = { page: params.page ?? 0, size: params.size ?? 50 };
    if (params.codigo) query.codigo = params.codigo;
    if (params.categoria) query.categoria = params.categoria;
    const res = await this.client<ApiResponse<Page<CertificadoTemplate>>>(this.base, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }
  async get(id: string): Promise<CertificadoTemplate> {
    const res = await this.client<ApiResponse<CertificadoTemplate>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: CertificadoTemplateCommand): Promise<CertificadoTemplate> {
    const res = await this.client<ApiResponse<CertificadoTemplate>>(this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: CertificadoTemplateCommand): Promise<CertificadoTemplate> {
    const res = await this.client<ApiResponse<CertificadoTemplate>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async deactivate(id: string): Promise<void> {
    await this.client(`${this.base}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
